<template>
  <q-page class="q-px-xl q-pb-lg bg-grey-1">
    <c-toast
      ref="toast"
      class="toast"
    />
    <div class="wrapper">
      <div class="pt-6 w-full">
        <h1 class="c-text-h2 c-text-gray-400 mb-16">
          Simulados Avulsos
        </h1>

        <!-- Verify -->
        <div>
          <div class="row col-12 mb-2">
            <div>
              <span class="c-text-subtitle mb-2"> Novo simulado avulso </span>
            </div>
          </div>
          <div class="row col-12">
            <div class="col-9">
              <c-input
                v-model="studentExamID"
                label="Informe o número do ID do simulado"
                :state="studentExamIDInputState"
                :info="studentExamIDInputInfo"
                @input="handlerOnChangeStudentExamID"
              >
                <template
                  v-if="studentExam"
                  slot="icon"
                >
                  <Icon
                    name="CheckMini"
                    outline
                    color="var(--c-color-green-300)"
                  />
                </template>
                <template
                  v-else-if="!studentExam && studentExamIDInputState === 'ERROR'"
                  slot="icon"
                >
                  <Icon
                    name="Error"
                    filled
                    color="var(--c-color-red-400)"
                  />
                </template>
                <template
                  v-else
                  slot="icon"
                >
                  <Icon
                    name="Test"
                    outline
                    color="var(--c-color-gray-300)"
                  />
                </template>
              </c-input>
            </div>
            <div class="col-3 c-d-flex">
              <c-button
                class="button-lg"
                :disabled="!hasValidStudentExamID"
                @click="registerSellableStudentExam"
              >
                <span> Iniciar Cadastro </span>
              </c-button>
            </div>
          </div>
        </div>

        <!-- Create -->
        <div
          v-if="studentExam"
          class="mt-5"
        >
          <div class="info-student-exam">
            <p class="c-text-b2 mb-1">
              <span class="text-weight-bold">Nome do simulado:</span>
              {{ studentExam.name }}
            </p>
            <p class="c-text-b2 mb-1">
              <span class="text-weight-bold">Autor:</span> {{ authorFullName }}
            </p>
            <p class="c-text-b2 mb-1">
              <span class="text-weight-bold">Duração:</span>
              {{ secondsToHm(studentExam.duration) }}
            </p>
          </div>
          <div class="form-sellable-student-exam mt-8">
            <SellableStudentExamFormItem
              class="mb-8"
              label="Início do simulado:"
              :date-time="formsShared.CREATE.beginDateTimeValue"
              @input="
                (value) => updateBeginDateTimeValue(value, CREATE, studentExam)
              "
            />
            <SellableStudentExamFormItem
              class="mb-8"
              label="Limites para encerramento do simulado:"
              :date-time="formsShared.CREATE.endDateTimeValue"
              :error="formsShared.CREATE.endDateTimeError"
              @input="(value) => updateEndDateTimeValue(value, CREATE)"
            />
            <SellableStudentExamFormItem
              label="Exibição do gabarito:"
              :date-time="formsShared.CREATE.showAnswersKeysDateTimeValue"
              :error="formsShared.CREATE.showAnswersKeysDateTimeError"
              @input="
                (value) => updateShowAnswersKeysDateTimeValue(value, CREATE)
              "
            />
            <div class="mt-6">
              <div class="row col-12 mb-2">
                <div>
                  <label class="c-text-btn mb-2">ID do produto</label>
                </div>
              </div>
              <div class="row col-12">
                <div class="col-8">
                  <c-input
                    v-model="productId"
                    label="Informe o ID do produto"
                  />
                </div>
                <div class="col-4 c-d-flex c-center">
                  <c-button
                    class="button-lg"
                    :disabled="!hasValidSchedule"
                    @click="scheduleSellableStudentExam"
                  >
                    <span> Agendar simulado avulso </span>
                  </c-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Listing -->
        <div class="mt-20">
          <span class="c-text-subtitle mb-6">
            Gerenciar simulados existentes
          </span>
          <c-table class="mb-8 list-table">
            <template slot="head">
              <c-table-row>
                <c-table-head
                  :direction="sortFilterByFieldName('name').order"
                  is-sortable
                  @change-order="sortBy(sortFilterByFieldName('name'))"
                >
                  Simulado
                </c-table-head>
                <c-table-head> Autor </c-table-head>
                <c-table-head
                  :direction="sortFilterByFieldName( 'begin_date').order"
                  is-sortable
                  @change-order="sortBy(sortFilterByFieldName( 'begin_date'))"
                >
                  Data de início
                </c-table-head>
                <c-table-head
                  :direction="sortFilterByFieldName('end_date').order"
                  is-sortable
                  @change-order="sortBy(sortFilterByFieldName('end_date'))"
                >
                  Encerramento
                </c-table-head>
                <c-table-head
                  class="list-table-head-status"
                  :direction="sortFilterByFieldName('status').order"
                  is-sortable
                  @change-order="sortBy(sortFilterByFieldName('enable'))"
                >
                  Status
                </c-table-head>
              </c-table-row>
            </template>
            <c-table-row
              v-for="(row, index) in sellableStudentExams"
              :key="`sellable-listing-table-row-${index}`"
              class="cursor-pointer"
              @click.native="handleEditSellableStudentExam(row)"
            >
              <c-table-data class="c-text-btn list-table-data">
                {{ row.name }}
              </c-table-data>
              <c-table-data class="c-text-b2 list-table-data">
                {{ row.author }}
              </c-table-data>
              <c-table-data class="c-text-b2 list-table-data">
                {{ timestampToDate(row.begin_date) }}
              </c-table-data>
              <c-table-data class="c-text-b2 list-table-data">
                {{ timestampToDate(row.end_date) }}
              </c-table-data>
              <c-table-data class="list-table-data">
                <div class="list-table-data-pill">
                  <template v-if="row.enable">
                    <c-pill
                      class="pill-ativa c-text-tag"
                      label="ATIVO"
                    />
                  </template>
                  <template v-else>
                    <c-pill
                      class="pill-encerrada c-text-tag"
                      label="ENCERRADO"
                    />
                  </template>
                </div>
              </c-table-data>
            </c-table-row>
            <c-table-row
              v-if="sellableStudentExams.length === 0"
              class="c-text-input c-text-gray-400"
            >
              <c-table-data class="c-text-b2">
                {{ emptySellableStudentExamsTable }}
              </c-table-data>
            </c-table-row>
          </c-table>

          <!-- Pagination -->
          <div class="row-pagination">
            <div class="per-page-dropdown">
              <c-dropdown
                label="Itens por página"
                placement="left-top"
                :options="perPageOptions"
                :value="pagination.perPage"
                @change="(value) => (pagination.perPage = value)"
              />
            </div>
            <c-paginator
              type="small"
              :pages="pagination.total"
              :page="pagination.current"
              @change="(val) => (pagination.current = val)"
            />
          </div>
        </div>
      </div>

      <!-- Editing -->
      <sub-menu
        ref="editDrawer"
        name="Editar Simulado"
        :right="true"
      >
        <template
          v-if="editedSellableStudentExam"
          v-slot:content
        >
          <div>
            <p class="c-text-b2 mb-3">
              <span class="text-weight-bold">Nome do simulado:</span>
              {{ editedSellableStudentExam.name }}
            </p>
            <p class="c-text-b2 mb-2">
              <span class="text-weight-bold">Autor:</span>
              {{ editedSellableStudentExam.author }}
            </p>
            <p class="c-text-b2 mb-2">
              <span class="text-weight-bold">Duração:</span>
              {{ secondsToHm(editedSellableStudentExam.duration) }}
            </p>
            <p class="c-text-b2 mb-2">
              <span class="text-weight-bold">ID do produto:</span>
              {{ editedSellableStudentExam.product_id }}
            </p>
          </div>
          <div class="bar mt-10" />
          <div class="form-sellable-student-exam mt-8 h-full">
            <SellableStudentExamFormItem
              class="mb-8"
              label="Início do simulado:"
              :date-time="formsShared.EDIT.beginDateTimeValue"
              @input="
                (value) =>
                  updateBeginDateTimeValue(
                    value,
                    EDIT,
                    editedSellableStudentExam
                  )
              "
            />
            <SellableStudentExamFormItem
              class="mb-8"
              label="Limites para encerramento do simulado:"
              :date-time="formsShared.EDIT.endDateTimeValue"
              :error="formsShared.EDIT.endDateTimeError"
              @input="(value) => updateEndDateTimeValue(value, EDIT)"
            />
            <SellableStudentExamFormItem
              label="Exibição do gabarito:"
              :date-time="formsShared.EDIT.showAnswersKeysDateTimeValue"
              :error="formsShared.EDIT.showAnswersKeysDateTimeError"
              @input="
                (value) => updateShowAnswersKeysDateTimeValue(value, EDIT)
              "
            />
          </div>
        </template>
        <template v-slot:footer>
          <div class="c-d-flex edit-footer-action row col-12">
            <div class="col-5">
              <c-button @click="recalculateScores">
                <div class="c-d-flex c-center">
                  <span>
                    <Icon
                      name="Medal"
                      outline
                      color="white"
                    />
                  </span>
                  <span> Recalcular ranking </span>
                </div>
              </c-button>
            </div>
            <div class="col-5">
              <c-button
                :disabled="!hasValidEdited"
                @click="saveEdit"
              >
                Salvar alterações
              </c-button>
            </div>
          </div>
        </template>
      </sub-menu>
    </div>
  </q-page>
</template>

<script>
import SellableStudentExamFormItem from '@/components/SellableStudentExamFormItem';
import SubMenu from '@/components/SubMenu';
import {
  CButton,
  CInput,
  Icon,
  CTable,
  CTableRow,
  CTableHead,
  CTableData,
  CDropdown,
  CPaginator,
  CPill,
} from '@estrategiahq/coruja-web-ui';
import { secondsToHm, formatDateToDMY } from '@/utils/date';
import { feedback, CREATE, EDIT } from './constants';

function createOrderStatusMap(label) {
  return {
    fieldName: label,
    order: '',
  };
}

const timestampToDate = formatDateToDMY('date');

const inputsShared = {
  beginDateTimeValue: null,
  endDateTimeValue: null,
  endDateTimeError: '',
  endDateModified: false,
  showAnswersKeysDateTimeValue: null,
  showAnswersKeysDateTimeError: '',
};
const formsShared = {
  CREATE: {
    ...inputsShared,
  },
  EDIT: {
    ...inputsShared,
  },
};

export default {
  components: {
    CTable,
    CTableRow,
    CTableHead,
    CTableData,
    CDropdown,
    CPaginator,
    CButton,
    CInput,
    CPill,
    Icon,
    SellableStudentExamFormItem,
    SubMenu,
  },
  props: {},
  data: () => ({
    studentExam: null,
    authorFullName: '',
    studentExamID: '',
    studentExamIDInputError: null,
    formsShared,
    CREATE,
    EDIT,
    productId: '',
    disableSchedule: false,
    sellableStudentExams: [],
    sortFilters: {},
    sortOptions: [
      createOrderStatusMap('name'),
      createOrderStatusMap('begin_date'),
      createOrderStatusMap('end_date'),
      createOrderStatusMap('status'),
    ],

    pagination: {
      current: 1,
      total: 1,
      sortBy: '',
      order: '',
      perPage: {
        label: '10',
        value: 10,
      },
    },
    emptySellableStudentExamsTable: feedback.fetchSellableStudentExams.empty,
    editedSellableStudentExam: null,
  }),
  computed: {
    perPageOptions() {
      const options = [10, 20, 30, 40, 50];
      return options.map((option) => ({
        label: `${option}`,
        value: option,
      }));
    },
    hasValidStudentExamID() {
      return this.studentExamID.length;
    },
    hasValidSchedule() {
      return (
        this.productId.length
        && this.formsShared.CREATE.beginDateTimeValue
        && this.formsShared.CREATE.endDateTimeValue
        && this.formsShared.CREATE.showAnswersKeysDateTimeValue
        && this.formsShared.CREATE.endDateTimeError.length === 0
        && this.formsShared.CREATE.showAnswersKeysDateTimeError.length === 0
        && !this.disableSchedule
      );
    },
    hasValidEdited() {
      return (
        this.formsShared.EDIT.endDateTimeError.length === 0
        && this.formsShared.EDIT.showAnswersKeysDateTimeError.length === 0
        && this.formsShared.EDIT.beginDateTimeValue
        && this.formsShared.EDIT.endDateTimeValue
        && this.formsShared.EDIT.showAnswersKeysDateTimeValue
      );
    },
    studentExamIDInputState() {
      return this.studentExamIDInputError
        && this.studentExamIDInputError.length > 1
        ? 'ERROR'
        : 'DEFAULT';
    },
    studentExamIDInputInfo() {
      return this.studentExamIDInputError
        && this.studentExamIDInputError.length > 1
        ? {
          message: this.studentExamIDInputError,
          icon: 'error',
          iconFilled: true,
        }
        : { message: '' };
    },
  },
  watch: {
    pagination: {
      deep: true,
      handler() {
        this.fetchSellableStudentExams();
      },
    },
  },
  mounted() {
    this.fetchSellableStudentExams();
  },
  methods: {
    secondsToHm,
    timestampToDate,
    updateBeginDateTimeValue(value, typeForm, studentExam) {
      this.formsShared[typeForm].beginDateTimeValue = value;
      if (!this.formsShared[typeForm].endDateModified && value) {
        const newEndDateTimeValue = new Date(value.getTime());
        newEndDateTimeValue.setSeconds(
          newEndDateTimeValue.getSeconds() + studentExam.duration
        );
        this.formsShared[typeForm].endDateTimeValue = newEndDateTimeValue;
      }
      this.validateFormItens(typeForm);
    },
    updateEndDateTimeValue(value, typeForm) {
      this.formsShared[typeForm].endDateTimeValue = value;
      this.formsShared[typeForm].endDateModified = true;
      this.validateFormItens(typeForm);
    },
    updateShowAnswersKeysDateTimeValue(value, typeForm) {
      this.formsShared[typeForm].showAnswersKeysDateTimeValue = value;
      this.validateFormItens(typeForm);
    },
    validateFormItens(typeForm) {
      if (
        this.formsShared[typeForm].endDateTimeValue
        && this.formsShared[typeForm].endDateTimeValue
          <= this.formsShared[typeForm].beginDateTimeValue
      ) {
        this.formsShared[typeForm].endDateTimeError = feedback.endDateTimeError;
      } else {
        this.formsShared[typeForm].endDateTimeError = '';
      }
    },
    showToast(type, text) {
      this.$refs.toast._show({
        type,
        text,
        timeout: 3000,
        position: 'top',
        width: 900,
      });
    },
    cleanCreateForm() {
      this.studentExam = null;
      this.author = null;
      this.formsShared.CREATE = {
        ...inputsShared,
      };
      this.productId = '';
    },
    cleanEditForm() {
      this.formsShared.EDIT = {
        ...inputsShared,
      };
      this.editedSellableStudentExam = null;
    },
    handlerOnChangeStudentExamID(value) {
      if (!value) {
        this.studentExamIDInputError = null;
      }
    },
    async registerSellableStudentExam() {
      try {
        this.cleanCreateForm();
        this.studentExamIDInputError = '';
        const studentExam = await this.$services.studentExamsRest.readOne(
          this.studentExamID
        );
        const author = await this.$services.accounts.getUserById(
          studentExam.owner_id
        );
        this.authorFullName = author.full_name;
        this.studentExam = studentExam;
        this.studentExamIDInputError = null;
      } catch (err) {
        if (err.tag === 'READ_STUDENT_EXAM_NOT_FOUND') {
          this.studentExamIDInputError = feedback.registerSellableStudentExamErrors.studentExamNotFound;
        } else if (err?.error?.tag === 'USER.NOT_FOUND') {
          this.studentExamIDInputError = feedback.registerSellableStudentExamErrors.userNotFound;
        } else {
          this.studentExamIDInputError = feedback.registerSellableStudentExamErrors.generic;
        }
        this.cleanCreateForm();
      }
    },
    async scheduleSellableStudentExam() {
      try {
        this.disableSchedule = true;
        const request = {
          studentExamId: this.studentExamID,
          beginDate: this.formsShared.CREATE.beginDateTimeValue.toISOString(),
          endDate: this.formsShared.CREATE.endDateTimeValue.toISOString(),
          answersKeyReleaseDate:
            this.formsShared.CREATE.showAnswersKeysDateTimeValue.toISOString(),
          productId: this.productId,
        };
        const response = await this.$services.sellableStudentExamsRest.create(
          request
        );
        if (response?.id) {
          this.showToast(
            'success',
            feedback.scheduleSellableStudentExam.success
          );
          setTimeout(() => {
            this.disableSchedule = false;
            this.fetchSellableStudentExams();
            this.studentExamID = '';
            this.cleanCreateForm();
          }, 1500);
        } else {
          let feedbackError;
          switch (response?.tag) {
            case 'SELLABLE_STUDENT_EXAM_DUPLICATED_KEY':
              feedbackError = feedback.scheduleSellableStudentExam.itemAlreadyExist;
              break;
            case 'SELLABLE_STUDENT_EXAM_INVALID_PRODUCT_ID':
              feedbackError = feedback.scheduleSellableStudentExam.invalidProductId;
              break;
            default:
              feedbackError = feedback.scheduleSellableStudentExam.genericError;
          }
          this.disableSchedule = false;
          this.showToast('error', feedbackError);
        }
      } catch (err) {
        this.disableSchedule = false;
        this.showToast('error', feedback.scheduleSellableStudentExam.genericError);
      }
    },
    async fetchSellableStudentExams() {
      const sortBy = this.sortOptions.find((option) => option.order !== '');
      try {
        this.emptySellableStudentExamsTable = feedback.fetchSellableStudentExams.empty;
        const response = await this.$services.sellableStudentExamsRest.list(
          this.pagination.perPage.value,
          this.pagination.current,
            sortBy?.fieldName || '',
            sortBy?.order || '',
        );
        if (response?.['sellable_student_exams'] && response?.total) {
          Promise.all(
            response.sellable_student_exams.map(
              async (sellableStudentExam) => ({
                name: sellableStudentExam.name,
                duration: sellableStudentExam.duration,
                author: await this.getAuthorFullName(
                  sellableStudentExam.sellable_info.author_id
                ),
                enable: this.isEnableSellableStudentExam(
                  new Date(sellableStudentExam.sellable_info.end_date)
                ),
                ...sellableStudentExam.sellable_info,
              })
            )
          ).then((sellableStudentExams) => {
            this.sellableStudentExams = sellableStudentExams;
          });
          this.pagination.total = Math.floor(
            (response.total + this.pagination.perPage.value - 1)
              / this.pagination.perPage.value
          );
        } else {
          this.emptySellableStudentExamsTable = feedback.fetchSellableStudentExams.empty;
        }
      } catch (err) {
        this.emptySellableStudentExamsTable = feedback.fetchSellableStudentExams.error;
      }
    },
    async getAuthorFullName(authorId) {
      let authorFullName;
      try {
        const author = await this.$services.accounts.getUserById(authorId);
        authorFullName = author.full_name;
      } catch (err) {
        if (err?.error?.tag === 'USER.NOT_FOUND') {
          authorFullName = feedback.getAuthorFullNameErrors.userNotFound;
        }
        authorFullName = feedback.getAuthorFullNameErrors.requestError;
      }
      return authorFullName;
    },
    async sortBy(sortableField) {
      const order = sortableField.order === 'ASC' ? 'DESC' : 'ASC';
      this.sortOptions.find(option => option.fieldName === sortableField.fieldName).order = order;
      this.sortOptions.map(option => {
        if (option.fieldName !== sortableField.fieldName && option.order !== '') {
          const tempOption = option;
          tempOption.order = '';
          return tempOption;
        }
        return option;
      });
      if (sortableField.fieldName === 'status') {
        this.sortWithoutSearch(sortableField.fieldName, sortableField.order);
      } else {
        await this.fetchSellableStudentExams();
      }
    },
    sortWithoutSearch(propName, sortDirection) {
      const direction = sortDirection === 'ASC' ? 1 : -1;
      this.sellableStudentExams = this.sellableStudentExams.sort(
        (a, b) => a[propName].toString().localeCompare(b[propName].toString())
              * direction
      );
    },
    sortFilterByFieldName(fieldName) {
      return this.sortOptions.find(option => option.fieldName === fieldName);
    },
    handleEditSellableStudentExam(sellableStudentExam) {
      this.cleanEditForm();
      this.editedSellableStudentExam = sellableStudentExam;
      this.formsShared.EDIT.beginDateTimeValue = new Date(
        sellableStudentExam.begin_date
      );
      this.formsShared.EDIT.endDateModified = true;
      this.formsShared.EDIT.endDateTimeValue = new Date(
        sellableStudentExam.end_date
      );
      this.formsShared.EDIT.showAnswersKeysDateTimeValue = new Date(
        sellableStudentExam.answers_key_release_date
      );
      this.$refs.editDrawer.open();
    },
    isEnableSellableStudentExam(endDate) {
      return new Date() < endDate;
    },
    async saveEdit() {
      try {
        const request = {
          beginDate: this.formsShared.EDIT.beginDateTimeValue.toISOString(),
          endDate: this.formsShared.EDIT.endDateTimeValue.toISOString(),
          answersKeyReleaseDate:
            this.formsShared.EDIT.showAnswersKeysDateTimeValue.toISOString(),
        };
        const response = await this.$services.sellableStudentExamsRest.edit(
          this.editedSellableStudentExam.id,
          request
        );
        if (response?.id) {
          this.editedSellableStudentExam.begin_date = response.begin_date;
          this.editedSellableStudentExam.end_date = response.end_date;
          this.editedSellableStudentExam.answers_key_release_date = response.answers_key_release_date;
          this.editedSellableStudentExam.enable = this.isEnableSellableStudentExam(
            new Date(response.end_date)
          );
          this.cleanEditForm();
          this.$refs.editDrawer.close();
          this.showToast('success', feedback.editSellableStudentExamSuccess);
        } else {
          this.showToast('error', feedback.editSellableStudentExamError);
        }
      } catch (err) {
        this.showToast('error', feedback.editSellableStudentExamError);
      }
    },
    async recalculateScores() {
      try {
        const response = await this.$services.studentExamsRest.recalculateScores(
          this.editedSellableStudentExam.student_exam_id
        );
        const updatedScores = response.updated_scores;
        this.showToast('success', `Total de [${updatedScores}] pontuações de estudantes recalculadas.`);
      } catch (err) {
        this.showToast('error', feedback.recalculateScoresSellableStudentExams.genericError);
      }
    },
  },
};
</script>
<style>
.button-lg {
  width: 100%;
  height: 3.5rem;
  margin-left: 1.5rem;
}
.toast {
  height: 77px;
}
.toast .c-toast-container__text {
  width: 100%;
  margin: 0;
}
.list-table {
  width: 100%;
}
.list-table .list-table-data {
  padding: 2.5rem 1.25rem;
}
.list-table-data-pill {
  display: flex;
  justify-content: center;
}
.list-table-head-status .c-table__th--align-left {
  text-align: center;
}
.c-pill.c-text-tag .c-pill__label {
  font-family: var(--c-font-family-primary);
  font-weight: var(--c-font-weight-bold);
  font-size: var(--c-font-size-dsk-tag);
  line-height: var(--c-font-line-dsk-tag);
}
.pill-ativa {
  color: var(--c-color-green-400) !important;
  background-color: #dcf9ed !important;
  border: 0 !important;
}
.pill-encerrada {
  color: var(--c-color-red-400) !important;
  background-color: #ffe6e6 !important;
  border: 0 !important;
}
.row-pagination {
  position: relative;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  display: flex;
}
.per-page-dropdown {
  position: absolute;
  left: 0;
}
.wrapper {
  display: flex;
}
.edit-footer-action {
  justify-content: space-evenly;
}
.edit-footer-action button {
  height: 44px;
  width: 100%;
}
</style>
